import { useEffect, useMemo, useRef, useState } from 'react';
import i18n from '@/i18n/config';
import { useLocation, useNavigate } from 'react-router-dom';
// removed Listbox usage in favor of custom menu
import {
  RiHome2Line,
  RiBuilding2Line,
  RiGlobalLine,
  RiUser3Line,
  RiArrowDownSLine,
  RiArrowDownWideLine,
  RiErrorWarningLine,
  RiShieldCheckLine,
  RiCloudLine,
  RiNotificationLine,
  RiFlaskLine,
  RiHistoryLine,
  RiDeleteBinLine,
  RiBarChartLine,
  RiUserHeartLine,
  RiSettings3Line,
  RiFileTextLine
} from '@remixicon/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip } from '@heroui/react';
import { useAtomValue, useSetAtom } from 'jotai';

import TopCard from '@/components/sidebar/TopCard';
import { PermissionGuard, useHasPermission } from '@/components/shared/PermissionGuard';
import { sidebarCollapsedAtom, sidebarHoveredAtom } from '@/store';

type MenuItem = {
  key: string;
  label: string;
  path: string;
  icon: (props: { className?: string }) => JSX.Element;
  permission?: string;
  children?: Array<{ key: string; label: string; path: string; permission?: string }>;
};

const rawMenu: MenuItem[] = [
  { key: 'dashboard', label: i18n.t('nav.dashboard'), path: '/', icon: (p) => <RiHome2Line {...p} /> },
  {
    key: 'workspaces',
    label: i18n.t('nav.workspaces'),
    path: '/organizations',
    icon: (p) => <RiBuilding2Line {...p} />,
    permission: 'Organization.index'
  },
  {
    key: 'domains',
    label: i18n.t('nav.domains'),
    path: '/domains',
    icon: (p) => <RiGlobalLine {...p} />,
    permission: 'Domain.index'
  },
  {
    key: 'experiments',
    label: i18n.t('nav.experiments'),
    path: '/experiments',
    icon: (p) => <RiFlaskLine {...p} />,
    permission: 'Experiment.index'
  },
  { key: 'users', label: i18n.t('nav.users'), path: '/users', icon: (p) => <RiUser3Line {...p} />, permission: 'User.index' },
  { key: 'roles', label: i18n.t('nav.roles'), path: '/roles', icon: (p) => <RiShieldCheckLine {...p} />, permission: 'Role.index' },
  {
    key: 'cms',
    label: i18n.t('nav.cms'),
    path: '/cms/plans',
    icon: (p) => <RiFileTextLine {...p} />,
    permission: 'Plan.index',
    children: [{ key: 'plans', label: i18n.t('nav.plans'), path: '/cms/plans', permission: 'Plan.index' }]
  },
  {
    key: 'cdn',
    label: i18n.t('nav.cdnManager'),
    path: '/cdn-manager',
    icon: (p) => <RiCloudLine {...p} />,
    permission: 'Cdn.index',
    children: [
      { key: 'providers', label: i18n.t('nav.providers'), path: '/cdn-providers', permission: 'CdnProvider.index' },
      { key: 'cdn', label: i18n.t('nav.cdn'), path: '/cdn', permission: 'Cdn.index' }
    ]
  },
  {
    key: 'relationships',
    label: i18n.t('nav.relationships'),
    path: '/relationships',
    icon: (p) => <RiUserHeartLine {...p} />,
    permission: 'Lead.index',
    children: [
      {
        key: 'leads',
        label: i18n.t('nav.leadsCollectors'),
        path: '/relationships/leads',
        permission: 'Lead.index'
      },
      {
        key: 'contact-messages',
        label: i18n.t('nav.contactMessages'),
        path: '/relationships/contact-messages',
        permission: 'ContactMessage.index'
      }
    ]
  },

  {
    key: 'reports',
    label: i18n.t('nav.reports'),
    path: '/reports',
    icon: (p) => <RiBarChartLine {...p} />,
    children: [
      {
        key: 'errorz',
        label: i18n.t('nav.errorz'),
        path: '/reports/errorz',
        permission: 'Errorz.index'
      },
      {
        key: 'blocked-users',
        label: i18n.t('nav.blockedUsers'),
        path: '/reports/blocked-users',
        permission: 'BlockedUser.index'
      },
      {
        key: 'notifications',
        label: i18n.t('nav.notifications'),
        path: '/reports/notifications',
        permission: 'Notification.index'
      },
      {
        key: 'activity-logs',
        label: i18n.t('nav.activityLogs'),
        path: '/reports/activity-logs',
        permission: 'ActivityLog.index'
      }
    ]
  },
  {
    key: 'settings',
    label: i18n.t('nav.settings'),
    path: '/settings',
    icon: (p) => <RiSettings3Line {...p} />,
    permission: 'ContactSection.index',
    children: [
      {
        key: 'contact-sections',
        label: i18n.t('nav.contactSections'),
        path: '/settings/contact-sections',
        permission: 'ContactSection.index'
      },
      {
        key: 'ai-models',
        label: i18n.t('nav.aiModels'),
        path: '/settings/ai-models',
        permission: 'AiModel.index'
      },
      {
        key: 'ai-usage',
        label: i18n.t('nav.aiUsage'),
        path: '/reports/ai-usage',
        permission: 'AiUsageLog.analytics'
      }
    ]
  },
  {
    key: 'trash',
    label: i18n.t('nav.trash'),
    path: '/trash',
    icon: (p) => <RiDeleteBinLine {...p} />,
    permission: 'Organization.index',
    children: [
      {
        key: 'trash-workspaces',
        label: i18n.t('nav.workspaces'),
        path: '/organizations/deleted',
        permission: 'Organization.index'
      },
      { key: 'trash-domains', label: i18n.t('nav.domains'), path: '/domains/deleted', permission: 'Domain.index' },
      {
        key: 'trash-sessions',
        label: i18n.t('nav.sessions'),
        path: '/sessions/deleted',
        permission: 'ExperimentSession.index'
      }
    ]
  }
];

export const Sidebar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isCollapsed = useAtomValue(sidebarCollapsedAtom);
  const isHovered = useAtomValue(sidebarHoveredAtom);
  const setCollapsed = useSetAtom(sidebarCollapsedAtom);
  const hasPermission = useHasPermission();

  const menu = useMemo(() => rawMenu, []);
  const parentsWithChildren = useMemo(() => menu.filter((m) => m.children && m.children.length > 0), [menu]);

  const findParentKeyByPath = (path: string): string | null => {
    for (const m of parentsWithChildren) {
      if (m.path === path) return m.key;
      if (m.children?.some((c) => c.path === path)) return m.key;
    }

    return null;
  };

  const [openKey, setOpenKey] = useState<string | null>(findParentKeyByPath(pathname));

  useEffect(() => {
    setOpenKey(findParentKeyByPath(pathname));
  }, [pathname]);

  // legacy listbox classes removed

  // no-op legacy handler removed

  const getChildrenSelectedKeys = (children: MenuItem['children']) => {
    if (!children) return new Set<string>();
    const hit = children.find((c) => c.path === pathname);

    return hit ? new Set([hit.path]) : new Set<string>();
  };

  const containerRef = useRef<HTMLDivElement | null>(null);

  const renderMenuItem = (item: MenuItem) => {
    const isParent = !!item.children && item.children.length > 0;
    const isOpen = openKey === item.key;
    const isChildSelected = isParent && getChildrenSelectedKeys(item.children).size > 0;
    const borderClass = isOpen ? 'border-s-3 border-primary' : 'border-s-3 border-transparent';
    const bgClass = isOpen ? 'bg-content1' : 'bg-transparent';
    const showContent = !isCollapsed || isHovered;

    if (!isParent) {
      const active = pathname === item.path;

      const menuButton = (
        <motion.button
          key={item.path}
          className={`group h-12 ${borderClass} flex w-full cursor-pointer items-center gap-4 px-3 text-start transition-colors ${
            active
              ? 'rounded-medium bg-background !text-primary hover:!cursor-default hover:bg-none'
              : 'hover:bg-default-100/60 rounded-none'
          }`}
          onClick={() => navigate(item.path)}
          whileHover={{ scale: isCollapsed && !isHovered ? 1.05 : 1 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.span
            className="inline-flex self-center"
            animate={{
              scale: isCollapsed && !isHovered ? 1.1 : 1,
              transition: { duration: 0.2 }
            }}
          >
            <item.icon className="h-5 w-5" />
          </motion.span>
          <AnimatePresence>
            {showContent && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className={`${active ? 'font-semibold' : 'group-hover:translate-x-1'} overflow-hidden text-end text-base whitespace-nowrap transition-transform md:text-[17px]`}
              >
                {item.label}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      );

      if (isCollapsed && !isHovered) {
        return (
          <Tooltip key={item.path} content={item.label} placement="right" showArrow>
            {menuButton}
          </Tooltip>
        );
      }

      return menuButton;
    }

    const parentButton = (
      <motion.button
        className="group hover:bg-default-100/60 flex h-12 w-full cursor-pointer items-center justify-between gap-2 px-3 text-start transition-colors"
        onClick={() => setOpenKey((prev) => (prev === item.key ? null : item.key))}
        whileHover={{ scale: isCollapsed && !isHovered ? 1.05 : 1 }}
        whileTap={{ scale: 0.95 }}
      >
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center">
            <motion.span
              className="inline-flex self-center"
              animate={{
                scale: isCollapsed && !isHovered ? 1.1 : 1,
                transition: { duration: 0.2 }
              }}
            >
              <item.icon className="h-5 w-5" />
            </motion.span>
            {isCollapsed && !isHovered && (
              <motion.span
                className="mt-1 inline-flex self-center"
                animate={{
                  scale: 1.1,
                  transition: { duration: 0.2 }
                }}
              >
                <RiArrowDownWideLine className="text-default-400 h-3 w-3" />
              </motion.span>
            )}
          </div>
          <AnimatePresence>
            {showContent && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className={`${isChildSelected ? 'text-foreground font-semibold' : ''} overflow-hidden text-end text-base whitespace-nowrap transition-transform group-hover:translate-x-1 md:text-[17px]`}
              >
                {item.label}
              </motion.span>
            )}
          </AnimatePresence>
        </div>
        <AnimatePresence>
          {showContent && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="inline-flex self-center"
            >
              <RiArrowDownSLine
                className={`text-default-400 h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    );

    const parentContent = (
      <div key={item.key} className={`rounded-medium mb-1 ${borderClass} ${bgClass}`}>
        {isCollapsed && !isHovered ? (
          <Tooltip content={item.label} placement="right" showArrow>
            {parentButton}
          </Tooltip>
        ) : (
          parentButton
        )}

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key={`submenu-${item.key}`}
              animate={{ height: 'auto', opacity: 1 }}
              className={`px-3 pt-1 pb-2 ${isCollapsed && !isHovered ? 'px-1' : ''}`}
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              transition={{ type: 'tween', duration: 0.2 }}
            >
              {/* Divider for collapsed state */}
              {isCollapsed && !isHovered && <div className="bg-divider mb-2 h-px"></div>}
              <div className={`flex w-full flex-col gap-1 ${isCollapsed && !isHovered ? 'pe-0' : 'pe-7'}`}>
                {(item.children ?? []).map((child) => {
                  const selected = pathname === child.path;

                  const childButton = (
                    <button
                      key={child.path}
                      className={`rounded-medium h-10 w-full ps-3 text-start text-base transition-colors ${
                        selected ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-default-200/70'
                      }`}
                      onClick={() => navigate(child.path)}
                    >
                      <div className="flex h-full w-full items-center justify-start gap-2 pe-3">
                        {child.label}
                      </div>
                    </button>
                  );

                  // In collapsed state, show submenus as small icon buttons
                  const collapsedChildButton = (
                    <Tooltip key={child.path} content={child.label} placement="right" showArrow>
                      <motion.button
                        className={`rounded-medium relative flex h-8 w-full items-center justify-center border-s-2 transition-colors ${
                          selected
                            ? 'bg-primary/10 text-primary border-primary'
                            : 'hover:bg-default-200/70 border-transparent'
                        }`}
                        onClick={() => navigate(child.path)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {/* Small indicator dot for submenu */}
                        <div className="bg-primary absolute top-1/2 -left-1 h-1 w-1 -translate-y-1/2 rounded-full"></div>
                        <span className={`text-xs font-bold ${selected ? 'text-primary' : 'text-default-400'}`}>
                          {child.label.charAt(0).toUpperCase()}
                        </span>
                        {/* Small dot on the other side for submenu */}
                        <div className="bg-default-400 absolute top-1/2 -right-1 h-1 w-1 -translate-y-1/2 rounded-full"></div>
                      </motion.button>
                    </Tooltip>
                  );

                  // Wrap child with PermissionGuard if it has a permission
                  if (child.permission) {
                    return (
                      <PermissionGuard key={child.path} permission={child.permission}>
                        {isCollapsed && !isHovered ? collapsedChildButton : childButton}
                      </PermissionGuard>
                    );
                  }

                  return isCollapsed && !isHovered ? collapsedChildButton : childButton;
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );

    return parentContent;
  };

  const canShowMenuItem = (item: MenuItem) => {
    if (!item.permission) return true;
    if (hasPermission(item.permission)) return true;
    if (
      item.children?.some((child) => {
        if (!child.permission) return true;
        return hasPermission(child.permission);
      })
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        // Check if current path has an active parent, keep it open
        const activeParentKey = findParentKeyByPath(pathname);
        setOpenKey(activeParentKey);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + B to toggle sidebar
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        setCollapsed(!isCollapsed);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [pathname, isCollapsed, setCollapsed]);

  return (
    <>
      {/* Mobile backdrop */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-30 bg-black/50 md:hidden"
            onClick={() => setCollapsed(true)}
          />
        )}
      </AnimatePresence>

      <aside className="sticky top-0 z-30 h-screen">
        <motion.div
          ref={containerRef}
          animate={{
            width: isCollapsed ? 63 : 256,
            x: isCollapsed ? -256 : 0
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="sidebar-container bg-background fixed flex h-full shrink-0 flex-col overflow-y-auto py-4 md:static md:translate-x-0 md:bg-transparent"
          style={
            {
              '--sidebar-x': isCollapsed ? '-256px' : '0px'
            } as React.CSSProperties
          }
        >
          <TopCard />

          {/* Custom menu with full-width submenus */}
          <div className="mt-4 pr-3">
            {menu.map((item) => {
              if (!canShowMenuItem(item)) {
                return null;
              }

              return renderMenuItem(item);
            })}
          </div>
        </motion.div>
      </aside>
    </>
  );
};
